import { useState } from "react";
import type { FormEvent } from "react";

import { useDialogForm, useDialogState, useToast } from "@/ds";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import {
  formatarStatusFinanceiro,
  previaProjecaoTransferencia,
  transferirEscopoMacro,
} from "@/modules/financeiro-obras";
import type {
  DecisaoProjecaoTransferencia,
  LookupRequestOptions,
  ObraDestinoTransferencia,
} from "@/modules/financeiro-obras";
import type { ColunaDestino } from "../etapa-destino-transferencia";
import { situacaoCurta } from "./transferir-escopo-macro-dialog.situacao";
import {
  useDetalheEscopoMacroDados,
  useObrasDestinoTransferenciaDados,
} from "../../../../hooks/use-planejamento-dados";
import { transferenciaEmFelZero } from "../planejamento-controle.cancelamento";
import { AVISO_TRANSFERENCIA_FEL_ZERO, NO_VALUE } from "../planejamento-controle.constants";
import {
  diasDoPeriodo,
  duracaoDoEscopo,
  formatarData,
  paraData,
  somarDias,
} from "../planejamento-controle.datas";
import { rotuloObra } from "../planejamento-controle.format";
import { JUSTIFICATIVA_OBRIGATORIA } from "@/shared/components";
import {
  testIdsServicosMateriais,
  type ServicosMateriaisSecaoProps,
} from "../servicos-materiais-secao";

import { origemDaProjecao } from "../projecao-transferencia";
import { useRevisaoProjecaoTransferencia } from "./use-revisao-projecao-transferencia";
import {
  ACEITE_OBRIGATORIO,
  CENTRO_CUSTO_OBRIGATORIO,
  DESTINO_OBRIGATORIO,
  TRANSFERIR_PENDENCIAS,
  CENTRO_CUSTO_REVISAO_LABEL,
  COLUNA_CONDICAO,
  COLUNA_ID,
  COLUNA_INICIO,
  COLUNA_OUTLOOK,
  COLUNA_SITUACAO,
  COLUNA_STATUS,
  COLUNA_TERMINO,
  COLUNA_TIPOS,
  SERVICOS_MATERIAIS_TITLE,
  DATAS_DESTINO,
  DATAS_OBRA,
  DATAS_ORIGEM,
  DESTINO_SUBTITLE,
  DESTINO_TITLE,
  DURACAO_DIAS,
  EMBARCACAO_NAO_INFORMADA,
  ENVIADO_PARA_APROVACAO,
  ESCOPO_ROTULO,
  ETAPA_DESTINO,
  ETAPA_REVISAO,
  FORA_PERIODO_AVISO,
  INDISPONIVEL_LABEL,
  MS01,
  MS02,
  MS03,
  POSICIONAMENTO_AVISO,
  PROJECAO_AVISO,
  SEM_CENTRO_CUSTO,
  SEM_INICIO_DESTINO_AVISO,
  SITUACAO_DISPONIVEL,
  TABELA_LABEL,
  TRANSFERIDO_MESSAGE,
  TRANSFERIR_FORM_ID,
} from "./transferir-escopo-macro-dialog.constants";
import type {
  ContextoDePara,
  EtapaTransferencia,
  LinhaDatas,
  TransferirEscopoMacroDialogProps,
  TransferirEstado,
} from "./types-transferir-escopo-macro-dialog";

type TransferirForm = { justificativa: string };

type Etapa = 1 | 2;

const hifenTipo = (tipoObra: string) => tipoObra.replace(" · ", " - ");
const dataOuTraco = (data: Date | null) => (data ? formatarData(data) : NO_VALUE);

const rotuloCentro = (centro: { codigo: string; nome: string }) =>
  `${centro.nome} · ${centro.codigo}`;

/**
 * Modal Transferir Escopo Macro (HU 2713, sem revisão de projeção): etapa Destino (Obras da mesma embarcação, indisponíveis
 * com o motivo, Centro de Custo receptor quando há mais de um) e etapa Revisão (de→para, datas no destino calculadas e
 * somente leitura, "concordo" quando o término passa do da Obra), depois a confirmação com a Justificativa. Voltar preserva as
 * escolhas; trocar de Obra refaz as datas.
 * O 400/409 do bff fica em `submitError` da confirmação.
 */
export function useTransferirEscopoMacroDialog({
  obraId,
  escopo,
  onClose,
  onConcluido,
}: TransferirEscopoMacroDialogProps) {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const consulta = useObrasDestinoTransferenciaDados(obraId, escopo.obraEscopoMacroId);
  const dados = consulta.data;
  const obras: ObraDestinoTransferencia[] = dados?.obras ?? [];
  const embarcacao =
    dados?.embarcacao?.nome ?? escopo.obraOrigem.embarcacao ?? EMBARCACAO_NAO_INFORMADA;

  const [etapa, setEtapa] = useState<Etapa>(1);
  const [destinoId, setDestinoId] = useState<string | null>(null);
  const [centroCustoId, setCentroCustoId] = useState("");
  const [aceite, setAceite] = useState(false);
  const [ajustando, setAjustando] = useState(false);
  const [tentouAvancar, setTentouAvancar] = useState(false);
  const [tentouTransferir, setTentouTransferir] = useState(false);
  const confirmacao = useDialogState();
  const descarte = useConfirmarDescarte({ onDescartar: onClose, testId: "transferir-descarte" });

  const destino = obras.find((obra) => obra.id === destinoId) ?? null;
  const felDestino = destino ? formatarStatusFinanceiro({ codigo: destino.fel }) : null;
  const centros = destino?.centrosCustoReceptores ?? [];
  const centro = centros.find((item) => item.id === centroCustoId) ?? null;
  const duracao = duracaoDoEscopo(escopo.dataInicio, escopo.dataFim);

  let estado: TransferirEstado = "pronto";
  if (consulta.status === "forbidden") estado = "proibido";
  else if (consulta.status === "error") estado = "erro";
  else if (consulta.status === "loading" || !dados) estado = "carregando";
  else if (obras.length === 0) estado = "vazio";

  function selecionarObra(id: string) {
    const obra = obras.find((item) => item.id === id);
    if (!obra?.disponivel || obra.id === destinoId) return;
    setDestinoId(obra.id);
    setCentroCustoId(
      obra.centrosCustoReceptores.length === 1 ? (obra.centrosCustoReceptores[0]?.id ?? "") : "",
    );
    setAceite(false);
  }

  // Início = início da Obra de destino; fim = início + duração original (dias corridos). Recalcula a cada troca de Obra.
  const periodoObra = destino
    ? { inicio: paraData(destino.periodo.dataInicio), fim: paraData(destino.periodo.dataFim) }
    : null;
  const inicio = periodoObra?.inicio ?? null;
  const fim = inicio ? somarDias(inicio, duracao) : null;
  const foraPeriodo = Boolean(fim && periodoObra?.fim && fim > periodoObra.fim);

  // Obra disponível sem centro de custo receptor é beco sem saída: não avança.
  const semCentroCusto = destino !== null && centros.length === 0;
  const destinoEscolhido =
    destino !== null && !semCentroCusto && (centros.length === 1 || centro !== null);

  const revisao = useRevisaoProjecaoTransferencia({
    obraId,
    escopoId: escopo.obraEscopoMacroId,
    tipoObraCodigo: escopo.tipoObraCodigo,
    destinoId,
    centroCustoId,
  });
  const detalheOrigem = useDetalheEscopoMacroDados(obraId, escopo.obraEscopoMacroId, etapa === 2);
  const origemProjecao = origemDaProjecao(detalheOrigem.data?.projecao);
  const previa = revisao.previa;
  const semProjecao = revisao.estado === "pronto" && previa?.projecao === null;
  const moedasSemCotacao = previa?.moedasSemCotacao ?? [];
  const transferirBloqueado = revisao.estado !== "pronto";
  const pendenciaDeRevisao = !inicio || moedasSemCotacao.length > 0;
  const faltaAceite = foraPeriodo && !aceite;

  const form = useDialogForm<TransferirForm>({
    initial: { justificativa: "" },
    validate: (valores) =>
      valores.justificativa.trim() === "" ? { justificativa: JUSTIFICATIVA_OBRIGATORIA } : {},
    onSubmit: async (valores) => {
      if (!destino || !centro) return;
      const body = {
        obraDestinoId: destino.id,
        centroCustoDestinoId: centro.id,
        foraPeriodoAceito: foraPeriodo && aceite,
        justificativa: valores.justificativa.trim(),
        ...(revisao.decisao ? { projecao: revisao.decisao } : {}),
      };
      const resposta = await comChave(body, (idempotencyKey) =>
        transferirEscopoMacro(obraId, escopo.obraEscopoMacroId, body, { idempotencyKey }),
      );
      if (resposta.efetivada) toast.success(TRANSFERIDO_MESSAGE);
      else toast.info(ENVIADO_PARA_APROVACAO(escopo.nome));
      onConcluido();
    },
  });

  const origem: ContextoDePara = {
    obra: `Obra ${escopo.obraOrigem.codigo} · ${escopo.obraOrigem.fel}`,
    embarcacao: escopo.obraOrigem.embarcacao ?? EMBARCACAO_NAO_INFORMADA,
    tipoObra: escopo.tipoObra,
    centroCusto: escopo.centroCusto,
  };
  const paraContexto: ContextoDePara | null = destino
    ? {
        obra: `Obra ${destino.codigo} · ${felDestino}`,
        embarcacao,
        tipoObra: escopo.tipoObra,
        centroCusto: centro ? rotuloCentro(centro) : CENTRO_CUSTO_REVISAO_LABEL,
      }
    : null;

  const etapas: EtapaTransferencia[] = [
    {
      numero: 1,
      titulo: ETAPA_DESTINO,
      situacao: etapa === 1 ? "active" : "done",
      ariaLabel: `Etapa 1: ${ETAPA_DESTINO}`,
      testId: "transferir-etapa-1",
      onSelect: etapa === 2 ? () => setEtapa(1) : undefined,
    },
    {
      numero: 2,
      titulo: ETAPA_REVISAO,
      situacao: etapa === 2 ? "active" : "pending",
      ariaLabel: `Etapa 2: ${ETAPA_REVISAO}`,
      testId: "transferir-etapa-2",
      onSelect: undefined,
    },
  ];

  const nomeCurto = escopo.nome.split(" - ")[0] ?? escopo.nome;
  const origemInicio = paraData(escopo.dataInicio);
  const origemFim = paraData(escopo.dataFim);
  const duracaoDestino = inicio && fim ? diasDoPeriodo(inicio, fim) : null;
  const duracaoObra =
    periodoObra?.inicio && periodoObra.fim
      ? diasDoPeriodo(periodoObra.inicio, periodoObra.fim)
      : null;
  const dias = (valor: number | null) => (valor === null ? NO_VALUE : DURACAO_DIAS(valor));
  const linhasDatas: LinhaDatas[] = [
    {
      chave: "origem",
      testId: "transferir-datas-origem",
      periodo: DATAS_ORIGEM(nomeCurto, escopo.obraOrigem.codigo),
      inicio: dataOuTraco(origemInicio),
      termino: dataOuTraco(origemFim),
      duracao: dias(origemInicio && origemFim ? diasDoPeriodo(origemInicio, origemFim) : null),
    },
    ...(destino
      ? [
          {
            chave: "destino",
            testId: "transferir-datas-destino",
            periodo: DATAS_DESTINO(nomeCurto, destino.codigo),
            inicio: dataOuTraco(inicio),
            termino: dataOuTraco(fim),
            duracao: dias(duracaoDestino),
          },
          {
            chave: "obra",
            testId: "transferir-datas-obra",
            periodo: DATAS_OBRA(destino.codigo),
            inicio: dataOuTraco(periodoObra?.inicio ?? null),
            termino: dataOuTraco(periodoObra?.fim ?? null),
            duracao: dias(duracaoObra),
          },
        ]
      : []),
  ];
  const mostrarAvisoFelZero = transferenciaEmFelZero(escopo.obraOrigem.fel, felDestino);

  function cancelar() {
    descarte.pedir(destinoId !== null);
  }

  return {
    formId: TRANSFERIR_FORM_ID,
    estado,
    errorMessage: consulta.errorMessage,
    recarregar: consulta.reload,
    etapa,
    etapas,
    cartao: {
      rotulo: ESCOPO_ROTULO,
      nome: escopo.nome,
      tipoObraCodigo: escopo.tipoObraCodigo,
      classificacao: escopo.classificacao,
      projecao: escopo.projecao,
      contexto: [escopo.tipoObra, embarcacao, escopo.centroCusto].join(" · "),
    },
    avisoSemObras: MS02(embarcacao),
    etapaDestino: {
      titulo: DESTINO_TITLE,
      subtitulo: DESTINO_SUBTITLE(hifenTipo(escopo.tipoObra)),
      aviso: MS01(embarcacao, escopo.obraOrigem.codigo, escopo.tipoObraCodigo),
      ariaLabel: TABELA_LABEL,
      obras,
      colunas: ["status", "tipos", "condicao", "inicio", "termino", "outlook"] as ColunaDestino[],
      textos: {
        colunaId: COLUNA_ID,
        colunaSituacao: COLUNA_SITUACAO,
        colunas: {
          status: COLUNA_STATUS,
          tipos: COLUNA_TIPOS,
          condicao: COLUNA_CONDICAO,
          inicio: COLUNA_INICIO,
          termino: COLUNA_TERMINO,
          outlook: COLUNA_OUTLOOK,
        },
        disponivel: SITUACAO_DISPONIVEL,
        indisponivel: INDISPONIVEL_LABEL,
        abreviarMotivo: (motivo: string) => situacaoCurta(motivo, escopo.tipoObraCodigo),
      },
      selecionadaId: destinoId,
      onSelecionar: selecionarObra,
      testIdPrefix: "transferir",
    },
    centroCusto: {
      mostrar: centros.length > 1,
      erro: semCentroCusto
        ? SEM_CENTRO_CUSTO
        : tentouAvancar && destino !== null && centros.length > 1 && centro === null
          ? CENTRO_CUSTO_OBRIGATORIO
          : null,
      value: centroCustoId || null,
      opcoes: centros.map((item) => ({ label: rotuloCentro(item), value: item.id })),
      escolher: (id: string) => setCentroCustoId(id),
    },
    origem,
    paraContexto,
    datas: linhasDatas,
    avisoPosicionamento:
      inicio && destino
        ? POSICIONAMENTO_AVISO(destino.codigo, formatarData(inicio), duracaoDestino ?? 0)
        : SEM_INICIO_DESTINO_AVISO,
    semInicioDestino: !inicio,
    foraPeriodo:
      foraPeriodo && periodoObra?.fim && destino
        ? {
            aviso: FORA_PERIODO_AVISO(
              nomeCurto,
              duracaoDestino ?? 0,
              destino.codigo,
              formatarData(periodoObra.fim),
            ),
            aceite,
            erro: tentouTransferir && faltaAceite ? ACEITE_OBRIGATORIO : null,
            concordar: (marcado: boolean) => setAceite(marcado),
          }
        : null,
    servicosMateriais: {
      titulo: SERVICOS_MATERIAIS_TITLE,
      itens: dados?.servicosMateriais ?? [],
      testIds: testIdsServicosMateriais("transferir"),
    } satisfies ServicosMateriaisSecaoProps,
    destinoErro: tentouAvancar && destino === null ? DESTINO_OBRIGATORIO : null,
    transferirErro:
      tentouTransferir && !faltaAceite && pendenciaDeRevisao ? TRANSFERIR_PENDENCIAS : null,
    proximaDesabilitada: estado !== "pronto",
    transferirDesabilitado: transferirBloqueado,
    financeiro: {
      estado:
        revisao.estado === "pronto"
          ? ("pronto" as const)
          : revisao.estado === "erro"
            ? ("erro" as const)
            : ("carregando" as const),
      errorMessage: revisao.errorMessage,
      recarregar: revisao.recarregar,
      previa,
      totalAtualTipo: revisao.totalAtualTipo,
      tipoObra: hifenTipo(escopo.tipoObra),
      escopoNome: escopo.nome,
      obraDestino: destino?.codigo ?? "",
    },
    projecaoCartao: {
      projecao: previa?.projecao ?? null,
      origem: origemProjecao,
      decisao: revisao.decisao,
      calculando: revisao.estado === "carregando",
      onAjustar: () => {
        if (origemProjecao) setAjustando(true);
      },
    },
    avisoSemProjecao: semProjecao ? PROJECAO_AVISO : null,
    ajustar:
      ajustando && origemProjecao && destino
        ? {
            variante: "escopo" as const,
            chavePrevia: `${obraId}/${escopo.obraEscopoMacroId}/${destino.id}/${centro?.id ?? ""}`,
            buscarPrevia: (
              decisao: DecisaoProjecaoTransferencia | null,
              opcoes: LookupRequestOptions,
            ) =>
              previaProjecaoTransferencia(
                obraId,
                escopo.obraEscopoMacroId,
                {
                  obraDestinoId: destino.id,
                  ...(centro?.id ? { centroCustoDestinoId: centro.id } : {}),
                  ...(decisao ? { projecao: decisao } : {}),
                },
                opcoes,
              ),
            cartao: {
              rotulo: ESCOPO_ROTULO,
              nome: escopo.nome,
              tipoObraCodigo: escopo.tipoObraCodigo,
              classificacao: escopo.classificacao,
              projecao: escopo.projecao,
              contexto: [escopo.tipoObra, embarcacao, escopo.centroCusto].join(" · "),
            },
            origem: origemProjecao,
            decisao: revisao.decisao,
            onAplicar: (decisao: DecisaoProjecaoTransferencia | null) => {
              revisao.aplicarDecisao(decisao);
              setAjustando(false);
            },
            onClose: () => setAjustando(false),
          }
        : null,
    avancar: () => {
      setTentouAvancar(true);
      if (!destinoEscolhido) return;
      setEtapa(2);
      revisao.iniciar();
    },
    voltar: () => setEtapa(1),
    abrirConfirmacao: () => {
      setTentouTransferir(true);
      if (!transferirBloqueado && !faltaAceite && !pendenciaDeRevisao) confirmacao.open();
    },
    confirmacao: {
      aberta: confirmacao.isOpen,
      mensagem: destino
        ? MS03(rotuloObra(destino.codigo, destino.nome), hifenTipo(escopo.tipoObra))
        : "",
      avisoEfetivacao: mostrarAvisoFelZero ? AVISO_TRANSFERENCIA_FEL_ZERO : null,
      justificativa: form.values.justificativa,
      justificativaError: form.errors.justificativa ?? null,
      isSubmitting: form.isSubmitting,
      submitError: form.submitError,
      digitarJustificativa: (texto: string) => form.setValue("justificativa", texto),
      enviar: (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        void form.submit();
      },
      cancelar: confirmacao.close,
    },
    cancelar,
    descarte: descarte.confirmacao,
  };
}
